import { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { EditorBuilderActions } from '@/components/EditorBuilder/types';
import { Blocks } from '@/components/PageEditor/types';
import { ContentType } from '@/modules/corporate/modules/course/views/CourseBuilder/components/Sidebar/types';

import { CourseActions, CourseError } from '../enums';
import {
  ActionPayloadType,
  onAddLessonPayloadType,
  onBackPayloadType,
  onBackToPathPayloadType,
  onImportLessonPayloadType,
  onMovePayloadType,
  onRemovePayloadType,
  onRenameItemPayloadType,
  onSetCommentThreadIdPayloadType,
  onSetImportDialog,
  onSetImportLessonAttrsPayloadType,
  onSetLessonAssigneePayloadType,
  onSetLessonCoverPayloadType,
  onSetLessonCustomDurationSavePayloadType,
  onSetLessonOptionalPayloadType,
  onSetLessonQuestionsPayloadType,
  onSetLessonUpdatedAtAttrPayloadType,
  onUpdateContentTextType,
  onUpdateLessonStatusPayloadType,
} from '../types';
import { getLessonPageId } from '../utils/adapt';
import {
  getItemId,
  getLessonDetails,
  getLessonsParent,
  getRootLessons,
  getSequentialSectionsAndLessons,
  onAddLesson,
  onAddSection,
  onImportLesson,
  onMove,
  onRemove,
  onRenameSectionOrLesson,
  onSetCommentThreadId,
  onSetImportLessonAttrs,
  onSetLessonAssignee,
  onSetLessonCover,
  onSetLessonCustomDurationSave,
  onSetLessonOptional,
  onSetLessonQuestions,
  onSetLessonUpdatedAtAttr,
  onUpdateContentText,
  onUpdateLessonStatus,
} from '../utils/courseActions';
import useCourseNavigation from './useCourseNavigation';
import useGoToHome from './useGoToHome';

const useCourseActions = (
  courseId: string,
  lessonPageId: string | null,
  data?: ContentType,
  setData?: (data: Partial<ContentType>) => void,
) => {
  const { navigate, onBack, onBackToPath } = useCourseNavigation(courseId);
  const { onBackToHome } = useGoToHome();
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const [importDialogOpen, { on, off }] = useToggle(false);

  const sequentialSectionsAndLessons = useMemo(() => getSequentialSectionsAndLessons(data), [data]);

  const { lessons, rootLessons, lessonsParent, sections } = useMemo(() => {
    const lessons = sequentialSectionsAndLessons.filter(
      (secOrLesson) => secOrLesson.type !== Blocks.SECTION,
    );
    const rootLessons = getRootLessons(data);
    const lessonsParent = getLessonsParent(data);
    const sections = sequentialSectionsAndLessons.filter(
      (secOrLesson) => secOrLesson.type === Blocks.SECTION,
    );

    return { lessons, rootLessons, lessonsParent, sections, sequentialSectionsAndLessons };
  }, [data, sequentialSectionsAndLessons]);

  const lesson = useMemo(() => {
    const _lessons = sequentialSectionsAndLessons.filter(
      (secOrLesson) => secOrLesson.type !== Blocks.SECTION,
    );

    const currentLessonIndex = _lessons.findIndex(
      (_lesson) => getLessonPageId(_lesson) === lessonPageId,
    );

    return currentLessonIndex === -1 ? undefined : _lessons[currentLessonIndex];
  }, [lessonPageId, sequentialSectionsAndLessons]);

  const currentLesson = useMemo(
    () => ({
      id: lesson?.attrs?.id,
      duration: lesson?.attrs?.duration,
      customDuration: lesson?.attrs?.customDuration,
      questionIds: lesson?.attrs?.questionIds,
      isOptional: lesson?.attrs?.isOptional,
      updatedAt: lesson?.attrs?.updatedAt,
      ...getLessonDetails(lesson?.content),
    }),
    [
      lesson?.attrs?.customDuration,
      lesson?.attrs?.duration,
      lesson?.attrs?.id,
      lesson?.attrs?.isOptional,
      lesson?.attrs?.questionIds,
      lesson?.attrs?.updatedAt,
      lesson?.content,
    ],
  );

  const allLessonBasicInfo = useMemo(
    () =>
      lessons.map((lesson) => ({
        id: lesson?.attrs?.id,
        ...getLessonDetails(lesson?.content),
      })),
    [lessons],
  );

  const onAction = useCallback(
    (type: CourseActions | EditorBuilderActions, payload?: ActionPayloadType) => {
      try {
        switch (type) {
          case CourseActions.AddSection: {
            const _lessonPageId = onAddSection(data, t)!;
            navigate(_lessonPageId);
            break;
          }
          case CourseActions.AddLesson: {
            const newLessonPageId = onAddLesson(
              payload as onAddLessonPayloadType,
              sections,
              data,
              lessons,
              t,
              courseId,
            );
            navigate(newLessonPageId);
            break;
          }
          case EditorBuilderActions.ImportLesson: {
            const newLessonPageId = onImportLesson(
              t,
              payload as onImportLessonPayloadType,
              sections,
              data,
            );
            navigate(newLessonPageId);
            break;
          }
          case CourseActions.RenameSectionOrLesson: {
            //get whether section or lesson
            const renameItemPayload = payload as onRenameItemPayloadType;
            const itemType = sequentialSectionsAndLessons?.find(
              (item) => getItemId(item) === renameItemPayload.id,
            )?.type as Blocks;

            onRenameSectionOrLesson(renameItemPayload, data, itemType, lessons);
            // This is used to update the updatedAt attribute of the lesson
            onSetLessonUpdatedAtAttr(
              { updatedAt: new Date().toISOString() } as onSetLessonUpdatedAtAttrPayloadType,
              lesson,
            );
            break;
          }
          case EditorBuilderActions.UpdateContentText: {
            onUpdateContentText(payload as onUpdateContentTextType, lesson?.content);
            // This is used to update the updatedAt attribute of the lesson
            onSetLessonUpdatedAtAttr(
              { updatedAt: new Date().toISOString() } as onSetLessonUpdatedAtAttrPayloadType,
              lesson,
            );
            break;
          }
          case CourseActions.SaveLessonCustomDuration: {
            onSetLessonCustomDurationSave(
              payload as onSetLessonCustomDurationSavePayloadType,
              lesson,
            );
            break;
          }
          case CourseActions.SetLessonQuestions: {
            onSetLessonQuestions(payload as onSetLessonQuestionsPayloadType, lesson);
            break;
          }
          case CourseActions.SetOptional: {
            onSetLessonOptional(payload as onSetLessonOptionalPayloadType, rootLessons);
            break;
          }
          case CourseActions.SetLessonCover: {
            onSetLessonCover(payload as onSetLessonCoverPayloadType, lesson?.content);
            break;
          }
          case EditorBuilderActions.SetLessonAssignee: {
            onSetLessonAssignee(payload as onSetLessonAssigneePayloadType, lesson?.content);
            break;
          }
          case EditorBuilderActions.SetImportLessonAttrs: {
            onSetImportLessonAttrs(payload as onSetImportLessonAttrsPayloadType, lesson?.content);
            off();
            break;
          }
          case EditorBuilderActions.UpdateLessonStatus: {
            onUpdateLessonStatus(payload as onUpdateLessonStatusPayloadType, lesson?.content);
            break;
          }
          case EditorBuilderActions.SetCommentThreadId: {
            onSetCommentThreadId(payload as onSetCommentThreadIdPayloadType, lesson?.content);
            break;
          }
          case CourseActions.Remove: {
            const onRemovePayload = payload as onRemovePayloadType;
            onRemove(onRemovePayload, lessonsParent, data, sequentialSectionsAndLessons, sections);
            //if selected lesson or section is removed, open prev lesson if present
            if (
              onRemovePayload?.id === lessonPageId ||
              onRemovePayload?.id === lessonsParent[lessonPageId!]
            ) {
              const reqList =
                onRemovePayload?.type === Blocks.SECTION ? sequentialSectionsAndLessons : lessons;
              const removedSectionOrLessonIndex = reqList.findIndex(
                (_lesson) => getItemId(_lesson) === onRemovePayload?.id,
              );
              if (
                (sequentialSectionsAndLessons.length <= 1 || lessons.length <= 1) &&
                removedSectionOrLessonIndex <= 0
              ) {
                navigate();
              } else {
                navigate(getLessonPageId(reqList[removedSectionOrLessonIndex - 1]));
              }
            }
            break;
          }
          case CourseActions.MoveLesson: {
            onMove(
              payload as onMovePayloadType,
              lessonsParent,
              data,
              sequentialSectionsAndLessons,
              sections,
              rootLessons,
            );
            break;
          }
          case CourseActions.Back: {
            onBack((payload as onBackPayloadType).messageSrc);
            break;
          }
          case CourseActions.BackToHome: {
            onBackToHome((payload as onBackPayloadType).messageSrc);
            break;
          }
          case CourseActions.BackToPath: {
            onBackToPath((payload as onBackToPathPayloadType).pathId);
            break;
          }
          case EditorBuilderActions.SetImportDialog: {
            if ((payload as onSetImportDialog).hide) {
              off();
            } else {
              on();
            }
            break;
          }
          case CourseActions.SetLessonUpdatedAtAttr: {
            onSetLessonUpdatedAtAttr(payload as onSetLessonUpdatedAtAttrPayloadType, lesson);
            break;
          }
          default:
            throw new Error('unhandled type');
        }
      } catch (e) {
        const err = e as Error;
        if (err.message === CourseError.SectionEmpty || err.message === CourseError.CourseEmpty) {
          showErrorSnackbar({ message: t(err.message) });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        }
      }
      setData?.({ ...data });
    },
    [
      data,
      lesson,
      lessonPageId,
      lessons,
      lessonsParent,
      navigate,
      off,
      on,
      onBack,
      onBackToHome,
      onBackToPath,
      rootLessons,
      sections,
      sequentialSectionsAndLessons,
      setData,
      showErrorSnackbar,
      t,
    ],
  );

  return {
    currentLesson,
    lessons,
    lessonsParent,
    onAction,
    rootLessons,
    importDialogOpen,
    allLessonBasicInfo,
  };
};

export default useCourseActions;
